/**
 * The anchor log: what the tracker, the recovery steps, the live proposal and
 * Apply saw and did, written to the console so a session can be read back
 * while the engine is being developed.
 *
 * Every event is one member of `AnchorEvent`, so the field names are decided
 * once here rather than at each call site; a new kind of event is a new
 * member, never a loose object.
 *
 * `record` is the one seam every anchor step reports through. It writes
 * nothing today: the events carry the contract's own wording (clause text), so
 * they leave the add-in through no channel. A test double that wants to see
 * them replaces `AnchorLog.record`.
 */
import type { AcceptedTextSpan } from "@common/utils/redline/redline-engine";
import type { RedlinePlacement } from "@common/utils/redline/wire/redline-placement";

import type { BodyReplacementPhase } from "./body-replacement";
import type { ReanchorResponse } from "./reanchor-client";
import type { RecoveryAttempt } from "./recovery/recovery-chain";

export type AnchorEvent =
    | {
        kind: "read";
        bookmarkNames: string[];
        paragraphCount: number;
        uniqueLocalIds: string[];
        firstText: string;
        lastText: string;
        previousText: string | null;
        nextText: string | null;
    }
    | { kind: "repair"; bookmarkNames: string[]; fix: "shrunk_to_paragraph_content"; locationRelation: string }
    | {
        kind: "repair";
        bookmarkNames: string[];
        fix: "edge_moved_off_neighbour";
        paragraphCount: number;
        trimmedStart: boolean;
        trimmedEnd: boolean;
        firstPartText: string | null;
        lastPartText: string | null;
    }
    | { kind: "repair"; bookmarkNames: string[]; fix: "edges_moved_to_words"; startAfterWord: string | null; endBeforeWord: string | null }
    | { kind: "recovery"; bookmarkNames: string[]; attempt: RecoveryAttempt }
    | { kind: "server"; bookmarkNames: string[]; response: ReanchorResponse | null }
    | { kind: "forget"; bookmarkNames: string[] }
    | {
        kind: "proposal";
        bookmarkNames: string[];
        source: string;
        placementKind: RedlinePlacement["kind"];
        paragraphsRead: number;
        coveredSpans: AcceptedTextSpan[];
        changeCount: number;
        readMs: number;
        engineMs: number;
        liveSkeleton: string | null;
        proposalSkeleton: string | null;
    }
    | {
        kind: "apply";
        bookmarkNames: string[];
        placementKind: RedlinePlacement["kind"];
        paragraphsRead: number;
        coveredSpans: AcceptedTextSpan[];
        changeCount: number;
        liveFirstText: string;
        liveLastText: string;
        desiredLastText: string;
    }
    | { kind: "settled_on_inserted"; bookmarkNames: string[]; insertedCount: number; placedAfterAnchor: boolean }
    | { kind: "watch"; bookmarkNames: string[]; eventsSupported: boolean }
    | { kind: "paragraph_events"; state: "unsupported" | "subscribed" | "changed" | "added" | "deleted"; uniqueLocalIds: string[] }
    | { kind: "body_replaced"; bookmarkNames: string[]; phase: BodyReplacementPhase }
    | { kind: "selection_watch"; selectionIds: string[]; changedIds: string[] }
    | { kind: "error"; bookmarkNames: string[]; step: string; message: string };

export class AnchorLog {
    /** Characters kept when a paragraph's text is quoted in an event. */
    static readonly EXCERPT_LENGTH = 60;

    /**
     * WHEN an anchor step has something to report
     *   CONSEQUENCE
     *     The event is accepted and dropped: no console line, no network call, so
     *     clause text never leaves the add-in through this seam. The parameter is
     *     read so the call sites keep their event shapes type-checked.
     */
    static record(event: AnchorEvent): void {
        void event.kind;
    }

    /** The first `length` characters of `text`, marked as cut when it was longer. */
    static excerptStart(text: string, length: number): string {
        return text.length <= length ? text : `${text.slice(0, length)}…`;
    }

    /** The last `length` characters of `text`, marked as cut when it was longer. */
    static excerptEnd(text: string, length: number): string {
        return text.length <= length ? text : `…${text.slice(-length)}`;
    }

    static messageOf(error: unknown): string {
        /**
         * CASES
         *   - error instanceof Error -> error.message
         *   - otherwise               -> String(error) for non-Error throws
         */
        return error instanceof Error ? error.message : String(error);
    }
}
